import { Box, Button, Flex, Text, useMediaQuery, VStack, Link, Image } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dropzone from 'react-dropzone';

import FormField from 'components/FormField';
import OrganizationSchema from 'validation/schemas/OrganisationSchema';
import UserBorderIcon from 'components/Icons/UserBorderIcon';
import Pencil from 'components/Icons/Pencil';
import {
  FetchUserOrganizationDetails,
  getUserData,
  getUserOrganizationData,
  SetTabs,
  UpdateUserOrganizationDetails,
} from 'redux/UserSlice';
import { GetOrgDataResponse, UpdateUserOrgDetailRequestBody } from 'models/UsersSlice.interface';
import { hasUserPermission } from 'utils/UserPermissions';
import { handleFileUpload } from 'services/s3Service';
import { acceptedFormats } from 'utils/FileFormats';
import UserBorderNew from 'components/Icons/UserBorderIconNew';

import UserShimmer from '../UserManagementShimmer';

const OrganisationFormNew = () => {
  const dispatch: any = useDispatch();
  const userData = useSelector(getUserData);
  const userOrganizationData: GetOrgDataResponse = useSelector(getUserOrganizationData);

  const [isSmallLaptop] = useMediaQuery('(max-width: 768px)');
  const [isLaptop] = useMediaQuery('(max-width: 1024px)');
  const [loader, setLoader] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [editUniqueName, setEditUniqueName] = useState(false);
  const userPermission = hasUserPermission(userData?.role, ['edit']);

  useEffect(() => {
    const getUserOrgDetails = async () => {
      await dispatch(FetchUserOrganizationDetails());
      setLoader(false);
    };
    getUserOrgDetails();
  }, []);

  const getChangedValue = (formValue: any) => {
    const requestBody: UpdateUserOrgDetailRequestBody = {};
    if (formValue.companyName !== userOrganizationData.company) {
      requestBody.company = formValue.companyName;
    }
    if (formValue.industry !== userOrganizationData.industry) {
      requestBody.industry = formValue.industry;
    }
    if (formValue.imageUrl !== userOrganizationData.logo_url) {
      requestBody.logo_url = formValue.imageUrl;
    }
    if (formValue.website !== userOrganizationData.website) {
      requestBody.website = formValue.website;
    }
    if (formValue.uniqueName !== userOrganizationData.unique_name) {
      requestBody.unique_name = formValue.uniqueName;
    }
    return {
      request_body: requestBody,
      toaster: true,
    };
  };

  const handleSubmit = async (formValue: any, setSubmitting: (data: boolean) => void) => {
    const payload = getChangedValue(formValue);
    if (Object.keys(payload.request_body).length > 0) {
      dispatch(UpdateUserOrganizationDetails(payload));
    }
    setSubmitting(false);
  };

  const handleImageUpload = async (data: any) => {
    dispatch(
      UpdateUserOrganizationDetails({
        request_body: {
          logo_url: data,
        },
        toaster: {
          successMessage: 'Image Uploaded Successfully',
        },
      }),
    );
  };

  const handleImageDrop = async (file: any, setFieldValue: any) => {
    setImageLoading(true);
    const data = await handleFileUpload(file[0], 'blox-org-profile');
    setImageLoading(false);
    setFieldValue('imageUrl', data);
    handleImageUpload(data);
  };

  if (loader) return <UserShimmer />;

  return (
    <Flex width={'100%'} height={'100%'} flexDir='column' bg='white'>
      <Formik
        initialValues={{
          orgId: userOrganizationData.id || 0,
          companyName: userOrganizationData.company || '',
          industry: userOrganizationData.industry || '',
          website: userOrganizationData.website || '',
          imageUrl: userOrganizationData.logo_url || '',
          uniqueName: userOrganizationData.unique_name || '',
          orgOwner: userOrganizationData.owner || { name: '', date_created: '' },
        }}
        onSubmit={(form, { setSubmitting }) => {
          handleSubmit(form, setSubmitting);
        }}
        validationSchema={OrganizationSchema}
      >
        {({ errors, touched, values, setFieldValue, isSubmitting }) => {
          return (
            <Form>
              <Box height={'100%'} width={'100%'} px={2}>
                {/* Header Section */}
                <Flex
                  width={'100%'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  mb={4}
                >
                  <Box>
                    <Text
                      fontFamily='Comfortaa bold'
                      color='#212121'
                      fontSize={isLaptop ? '16px' : '20px'}
                      fontWeight='bold'
                      mb='2'
                    >
                      Organisation
                    </Text>
                    <Text color='#787878' fontSize='14px' fontFamily='regular_roboto'>
                      Manage your organisation ID
                    </Text>
                  </Box>
                </Flex>

                <Box
                  width={'100%'}
                  height={`calc(100% - ${isLaptop ? '120px' : '140px'})`}
                  overflowY='auto'
                  px={2}
                  gap={4}
                >
                  {/* Organisation Logo Section - Above */}
                  <Flex
                    width={'100%'}
                    // flexDirection={''}
                    alignItems={'flex-start'}
                    mb={isLaptop ? '6' : '8'}
                  >
                    {values.imageUrl ? (
                      <Box
                        width={'66px'}
                        height={'66px'}
                        borderRadius={'50%'}
                        overflow='hidden'
                        mb='4'
                        boxShadow={'1px 1px 10px #d9d9d9'}
                      >
                        <Image
                          src={values.imageUrl}
                          width={'100%'}
                          height={'100%'}
                          objectFit='cover'
                        />
                      </Box>
                    ) : (
                      <Box
                        width={'66px'}
                        height={'66px'}
                        borderRadius={'50%'}
                        bg='#F5F5F5'
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        mb='4'
                      >
                        <UserBorderNew />
                      </Box>
                    )}
                    <VStack spacing='2' alignItems={'flex-start'} pt={2} pl={2}>
                      <Dropzone
                        onDrop={(file) => handleImageDrop(file, setFieldValue)}
                        multiple={false}
                        disabled={!userPermission || isSubmitting || imageLoading}
                        accept={acceptedFormats}
                      >
                        {({ getRootProps, getInputProps }) => (
                          <Box
                            {...getRootProps()}
                            cursor={
                              !userPermission || isSubmitting || imageLoading
                                ? 'not-allowed'
                                : 'pointer'
                            }
                            opacity={!userPermission || isSubmitting || imageLoading ? 0.5 : 1}
                          >
                            <input {...getInputProps()} />
                            <Link
                              color='#6B5FC6'
                              textDecoration='underline'
                              fontSize='16px'
                              fontWeight='600'
                              fontFamily='regular_roboto'
                              _hover={{ textDecoration: 'underline' }}
                            >
                              Remove or upload new
                            </Link>
                          </Box>
                        )}
                      </Dropzone>
                      <Text
                        color='#6A6A6A'
                        fontSize='14px'
                        fontWeight='400'
                        fontFamily='regular_roboto'
                        textAlign='left'
                      >
                        Professional logo recommended (JPG, PNG up to 5MB)
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Form Fields Section - Below Image */}
                  <Box width={'100%'}>
                    <VStack
                      spacing='5'
                      alignItems={'flex-start'}
                      width={'100%'}
                      fontFamily='regular_roboto'
                    >
                      {/* Organisation ID and Company Name Row */}
                      <Flex
                        alignItems={'flex-start'}
                        gap='5'
                        width={'100%'}
                        flexDir={isSmallLaptop ? 'column' : 'row'}
                      >
                        <Box flex='1' position='relative'>
                          <FormField
                            label='Organization ID'
                            name='uniqueName'
                            type='text'
                            labelStyles={{
                              fontSize: '12px',
                              color: '#82736C',
                              fontWeight: '600',
                            }}
                            inputStyles={{
                              maxWidth: '100%',
                              boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                              height: '35px',
                              paddingInlineStart: '8px',
                              paddingInlineEnd: '35px',
                              fontWeight: '500',
                              color: '#212121',
                            }}
                            placeholder='Organization ID'
                            error={errors.uniqueName}
                            touched={touched.uniqueName}
                            disabled={!editUniqueName || !userPermission || isSubmitting}
                          />
                          {userData?.role === 'owner' && (
                            <Box
                              position='absolute'
                              right='8px'
                              bottom='8px'
                              cursor='pointer'
                              onClick={() => setEditUniqueName(!editUniqueName)}
                            >
                              <Pencil style={{ cursor: 'pointer' }} color='grey.100' />
                            </Box>
                          )}
                        </Box>
                        <Box flex='1'>
                          <FormField
                            label='Company name'
                            name='companyName'
                            type='text'
                            labelStyles={{
                              fontSize: '12px',
                              color: '#82736C',
                              fontWeight: '600',
                            }}
                            inputStyles={{
                              maxWidth: '100%',
                              boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                              height: '35px',
                              paddingInlineStart: '8px',
                              fontWeight: '500',
                              color: '#212121',
                            }}
                            placeholder='Enter company name'
                            error={errors.companyName}
                            touched={touched.companyName}
                            disabled={!userPermission || isSubmitting}
                          />
                        </Box>
                      </Flex>

                      {/* Industry and Website Row */}
                      <Flex
                        alignItems={'flex-start'}
                        gap='5'
                        width={'100%'}
                        flexDir={isSmallLaptop ? 'column' : 'row'}
                        mt='.7rem'
                      >
                        <Box flex='1'>
                          <FormField
                            label='Industry'
                            name='industry'
                            type='text'
                            labelStyles={{
                              fontSize: '12px',
                              color: '#82736C',
                              fontWeight: '600',
                            }}
                            inputStyles={{
                              maxWidth: '100%',
                              boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                              height: '35px',
                              paddingInlineStart: '8px',
                              fontWeight: '500',
                              color: '#212121',
                            }}
                            placeholder='Enter industry name'
                            error={errors.industry}
                            touched={touched.industry}
                            disabled={!userPermission || isSubmitting}
                          />
                        </Box>
                        <Box flex='1'>
                          <FormField
                            label='Website'
                            name='website'
                            type='text'
                            labelStyles={{
                              fontSize: '12px',
                              color: '#82736C',
                              fontWeight: '600',
                            }}
                            inputStyles={{
                              maxWidth: '100%',
                              boxShadow: '0px 0px 0px 1.5px #8B8AAE',
                              height: '35px',
                              paddingInlineStart: '8px',
                              fontWeight: '500',
                              color: '#212121',
                            }}
                            placeholder='Enter website name'
                            error={errors.website}
                            touched={touched.website}
                            disabled={!userPermission || isSubmitting}
                          />
                        </Box>
                      </Flex>

                      {/* Org Owner Section */}
                      <Box mt='.7rem !important' width='300px'>
                        <Text fontSize='12px' color='#82736C' fontWeight='600' mb='2'>
                          Org Owner
                        </Text>
                        <Flex
                          minW='250px'
                          padding='.7rem'
                          borderRadius='10px'
                          boxShadow='0px 0px 0px 1.5px #8B8AAE'
                          flexDirection='column'
                          gap='2'
                        >
                          <Flex alignItems='center'>
                            {userOrganizationData.owner?.profile_image_url ? (
                              <Image
                                boxShadow={'1px 1px 10px #d9d9d9'}
                                src={userOrganizationData.owner.profile_image_url}
                                borderRadius={'50%'}
                                height={12}
                                width={12}
                              />
                            ) : (
                              <UserBorderIcon height={12} width={12} />
                            )}
                            <Box ml='4' flex='1'>
                              <Text color='black' fontWeight='600'>
                                {values.orgOwner.name}
                              </Text>
                              <Text fontSize='xs' color='grey.100'>
                                Created : {values.orgOwner.date_created}
                              </Text>
                              {userData?.role === 'owner' && (
                                <Button
                                  cursor='pointer'
                                  onClick={() => {
                                    dispatch(SetTabs(9));
                                  }}
                                  width='fit-content'
                                  p='0'
                                  mt='2'
                                  color='#6B5FC6'
                                  disabled={!userPermission}
                                  bg='transparent'
                                  _hover={{ bg: 'transparent' }}
                                  leftIcon={<Pencil width={4} height={4} color='#6B5FC6' />}
                                  fontSize='14px'
                                  fontWeight='500'
                                  textDecoration='underline'
                                >
                                  Modify owner
                                </Button>
                              )}
                            </Box>
                          </Flex>
                        </Flex>
                      </Box>
                    </VStack>
                  </Box>
                </Box>

                {/* Footer Section */}
                <Flex width={'100%'} gap='4' pt='4' borderTop='1px solid #E5E5E5'>
                  <Button
                    flex='1'
                    h='40px'
                    borderRadius='10px'
                    fontSize='15px'
                    color='#212121'
                    bgColor='#F1EEFF'
                    px='6'
                    onClick={() => {
                      dispatch(SetTabs(1));
                    }}
                    _hover={{ bgColor: '#EFEEFD' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    _hover={{ _disabled: {} }}
                    disabled={
                      imageLoading ||
                      Object.keys(errors).length > 0 ||
                      !userPermission ||
                      isSubmitting
                    }
                    type='submit'
                    flex='1'
                    h='40px'
                    borderRadius='10px'
                    fontSize='15px'
                    bgColor='#6B5FC6'
                    color='white'
                    px='6'
                    isLoading={isSubmitting}
                    loadingText='Saving...'
                  >
                    Save Changes
                  </Button>
                </Flex>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Flex>
  );
};

export default OrganisationFormNew;
